<template>
  <div class="min-vh-70 d-flex flex-column flex-md-row">
    <div class="bg-gray-21 py-3 py-md-4 px-0 px-md-5 navigation-container">
      <div class="px-4">
        <div class="mt-4 navigation-container__steps">
          <navigation-header :currentStepId="currentStepId" :steps="steps" />
        </div>
      </div>
    </div>
    <div class="flex-grow-1 bg-white p-4">
      <div class="min-height-content w-auto">
        <circular-letter v-if="isCircularLetterView" ref="circularLetterRef" />
        <request-confirmation
          v-if="isRequestConfirmationView"
          :request-number="requestNumber"
        />
        <keep-alive>
          <component
            ref="currentTabRef"
            :is="currentStep.component"
            :key="`component-${currentStep.id}`"
            @input="() => {}"
            @finish="onFinishHandler"
          />
        </keep-alive>
      </div>
      <div>
        <hr />
        <navigation-footer
          :steps="steps"
          :current-step-id="currentStepId"
          @cancel="onCancelHandler"
          @start="onStartHandler"
          @next="onNextHandler"
          @prev="onPrevHandler"
          @complete="onCompleteHandler"
          @finish="onFinishHandler"
          hide-finish-button
        />
      </div>
    </div>

    <!-- Create certification evidence reminder modal -->
    <certification-evidence-reminder-modal
      ref="myCertificationEvidenceReminderRef"
      @close="onCloseCertEvidenceReminderModal"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';

//components
import { NavigationHeader, NavigationFooter } from './components/navigation';
import {
  CircularLetter,
  RequestConfirmation,
  ApplicantInformation,
  ResponsibleInformation,
  ResearchInformation,
  ResearchObjectives,
  DocumentValidation,
  TermsAndCondition,
} from './components/steps';
import { CertificationEvidenceReminderModal } from './components';

//utils
import { i18n } from '@/i18n.js';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

//services
import {
  saveRequest as _saveRequest,
  getRequestAutoSave as _getRequestAutoSave,
  saveRequestAutoSave as _saveRequestAutoSave,
} from '@/services/app.api.js';

//constants
import requestFormSteps from '@/utils/constants/requestFormSteps.js';
import requestSchoolTypes from '@/utils/constants/requestSchoolTypes';
import attachmentCategories from '@/utils/constants/attachmentCategories.js';
import { itemSubTypes } from '@/utils/constants/itemSubTypes.js';

//vuex types
import types from '@/store/modules/app/types.js';
import { name as appModuleName } from '@/store/modules/app';

export default {
  name: 'CreateRequestView',
  mixins: [alertMixin],
  components: {
    NavigationHeader,
    NavigationFooter,
    CertificationEvidenceReminderModal,
    CircularLetter,
    RequestConfirmation,
    ApplicantInformation,
    ResearchInformation,
    ResearchObjectives,
  },
  data: () => ({
    currentStepId: requestFormSteps.UNSET,
    model: {
      step: {
        [requestFormSteps.APPLICANT_INFORMATION]: null,
        [requestFormSteps.RESPONSIBLE_INFORMATION]: null,
        [requestFormSteps.RESEARCH_INFORMATION]: null,
        [requestFormSteps.RESEARCH_OBJECTIVES]: null,
        [requestFormSteps.DOCUMENT_VALIDATION]: null,
        [requestFormSteps.TERMS_AND_CONDITIONS]: null,
      },
    },
    requestNumber: null,
    autoSaveData: null,
  }),
  computed: {
    steps() {
      return [
        {
          id: 1,
          title: i18n.t('ApplicantInformation'),
          component: ApplicantInformation,
        },
        {
          id: 2,
          title: i18n.t('InformationOfTheThesisChair'),
          component: ResponsibleInformation,
        },
        {
          id: 3,
          title: i18n.t('ResearchInformation'),
          component: ResearchInformation,
        },
        {
          id: 4,
          title: i18n.t('ObjectivesOfYourResearch'),
          component: ResearchObjectives,
        },
        {
          id: 5,
          title: i18n.t('AttachDocuments'),
          component: DocumentValidation,
        },
        {
          id: 6,
          title: i18n.t('TermsAndConditions'),
          component: TermsAndCondition,
        },
      ];
    },
    currentStep() {
      return this.steps.find((x) => x.id === this.currentStepId) || {};
    },
    isCircularLetterView() {
      return this.currentStepId === requestFormSteps.UNSET;
    },
    isRequestConfirmationView() {
      return (
        this.currentStepId === requestFormSteps.REQUEST_CONFIRMATION &&
        this.requestNumber
      );
    },
  },
  async mounted() {
    this[types.SET_SITEMAP_INFO]({
      routes: [i18n.t('Home'), i18n.t('CreationOfTheRequest')],
    });
    await this.getRequestAutoSave();
  },
  methods: {
    ...mapMutations(appModuleName, [types.SET_SITEMAP_INFO]),
    getCurrentStepModel() {
      return this.$refs.currentTabRef.getModel();
    },
    setCurrentStepModel(payload) {
      return this.$refs.currentTabRef.setModel(payload);
    },
    async getRequestAutoSave() {
      const autoSaveResponse = (await _getRequestAutoSave()).data;
      if (autoSaveResponse && Object.values(autoSaveResponse)) {
        this.autoSaveData = {
          userId: autoSaveResponse.userId,
          activeStep: autoSaveResponse.activeStep,
          data: JSON.parse(autoSaveResponse.data),
        };
      }
    },
    async validateForm() {
      if (this.$refs.currentTabRef.validate) {
        const isValid = await this.$refs.currentTabRef.validate();
        if (!isValid) {
          this.ShowToast(
            i18n.t('Alert'),
            `${i18n.t('PleaseCompleteRequestedInformation')}.`,
            'error'
          );
          return;
        }
        return true;
      }
    },
    onCancelHandler() {
      this.$router.replace('/');
    },
    async onStartHandler() {
      if (this.$refs.circularLetterRef.validate) {
        const isValid = await this.$refs.circularLetterRef.validate();
        if (!isValid) {
          this.ShowToast(
            i18n.t('Alert'),
            `${i18n.t('PleaseCompleteRequestedInformation')}.`,
            'error'
          );
          return;
        }
      }
      this.currentStepId = requestFormSteps.APPLICANT_INFORMATION;
    },
    onPrevHandler() {
      if (this.currentStepId === requestFormSteps.APPLICANT_INFORMATION) {
        this.currentStepId = requestFormSteps.UNSET;
        return;
      }
      this.currentStepId -= 1;
    },
    async onNextHandler() {
      //validate all required fields
      const allFieldsAreCompleted = await this.validateForm();
      if (!allFieldsAreCompleted) {
        return;
      }

      //set step model
      this.model.step[`${this.currentStepId}`] = this.getCurrentStepModel();

      //eval to auto save steps
      if (
        [
          requestFormSteps.RESPONSIBLE_INFORMATION,
          requestFormSteps.RESEARCH_INFORMATION,
          requestFormSteps.RESEARCH_OBJECTIVES,
        ].includes(this.currentStepId)
      ) {
        const modelToSaveInfo = this.getModelToSaveInfo(this.currentStepId);
        await _saveRequestAutoSave({
          activeStep: this.currentStepId,
          data: modelToSaveInfo,
        });
      }

      //move on
      this.currentStepId += 1;

      //set model info to next step
      await this.$nextTick(() => {
        this.setCurrentStepModel(
          this.getStepModelFromSavedInfo(this.currentStepId)
        );
      });

      if (this.currentStepId === requestFormSteps.DOCUMENT_VALIDATION) {
        this.onOpenCertEvidenceReminderModal();
      }
    },
    onCompleteHandler() {},
    async onFinishHandler() {
      //validate all required fields
      const allFieldsAreCompleted = await this.validateForm();
      if (!allFieldsAreCompleted) {
        return;
      }

      const modelStep1 =
        this.model.step[requestFormSteps.APPLICANT_INFORMATION];
      const modelStep2 =
        this.model.step[requestFormSteps.RESPONSIBLE_INFORMATION];
      const modelStep3 = this.model.step[requestFormSteps.RESEARCH_INFORMATION];
      const modelStep4 = this.model.step[requestFormSteps.RESEARCH_OBJECTIVES];
      const modelStep5 = this.model.step[requestFormSteps.DOCUMENT_VALIDATION];

      const payload = {
        //step 1
        fullName: modelStep1?.fullName,
        fatherLastName: modelStep1?.fatherLastName,

        //step 2
        mentorFullName: modelStep2?.mentorFullName,
        mentorTelephone: modelStep2?.mentorTelephone,
        mentorWorkPlace: modelStep2?.mentorWorkPlace,
        mentorEmail: modelStep2?.mentorEmail,
        mentorWorkPosition: modelStep2?.mentorWorkPosition,
        mentorCellPhone: modelStep2?.mentorCellPhone,

        //step 3
        requestTypeId: modelStep3?.requestTypeId,
        schoolData: modelStep3?.schoolIds.map((x) => ({ schoolId: +x })),
        schoolDependencies: modelStep3?.schoolDependencies,
        requestSolicitorId: modelStep3?.requestSolicitorId,
        requestSolicitorUniversityId: modelStep3?.university?.id || null,
        requestSolicitorDescription:
          modelStep3?.requestSolicitorId !== requestSchoolTypes.school
            ? modelStep3?.requestSolicitorDescription
            : modelStep3?.university?.name,

        //step 4
        investigationTitle: modelStep4?.investigationTitle,
        investigationObjective: modelStep4?.investigationObjective,
        investigationMethodology: modelStep4?.investigationMethodology,
        investigationSamplingDescription:
          modelStep4?.investigationSamplingDescription,

        //step 5
        investigationImportanceDescription:
          modelStep5?.investigationImportanceDescription,
        approvedParents: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationImpactsGuardian
        ),
        approvedDirectors: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationImpactsPrincipal
        ),
        approvedStudents: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationImpactsStudents
        ),
        approvedTeachers: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationImpactsTeachers
        ),
        approvedSharedTeachers: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationShareTeachers
        ),
        approvedGeneral: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationInpactsGeneral
        ),
      };

      const { data: saveResponse } = await _saveRequest(payload).catch(
        (error) => {
          this.ShowToast('', error.response.data.message, 'error');
        }
      );
      if (saveResponse) {
        this.ShowToast(
          'Proceso exitoso',
          'El caso ha sido creado exitosamente.',
          'success'
        );
        this.requestNumber = saveResponse;
        this.currentStepId = requestFormSteps.REQUEST_CONFIRMATION;
      }
    },
    onOpenCertEvidenceReminderModal() {
      this.$refs.myCertificationEvidenceReminderRef?.showModal();
    },
    onCloseCertEvidenceReminderModal() {
      this.$refs.myCertificationEvidenceReminderRef?.hideModal();
    },
    getStepModelFromSavedInfo(stepId) {
      if (this.autoSaveData?.data) {
        const claimStepKeyId = `ClaimStep${stepId}`;
        const claimStep = this.autoSaveData.data[claimStepKeyId];

        //RESPONSIBLE_INFORMATION
        if (stepId === requestFormSteps.RESPONSIBLE_INFORMATION && claimStep) {
          return {
            mentorFullName: claimStep.mentorFullName,
            mentorWorkPosition: claimStep.mentorJobPosition,
            mentorWorkPlace: claimStep.mentorWorkplace,
            mentorTelephone: claimStep.mentorPhoneNumber,
            mentorCellPhone: claimStep.mentorCellphone,
            mentorEmail: claimStep.mentorEmail,
          };
        }

        //RESEARCH_INFORMATION
        if (stepId === requestFormSteps.RESEARCH_INFORMATION && claimStep) {
          const requestSolicitorId = claimStep.studySchool1
            ? requestSchoolTypes.school
            : claimStep.organizationName1
            ? requestSchoolTypes.organization
            : requestSchoolTypes.other;

          const schoolObjectList =
            typeof claimStep.gvSchool === 'string'
              ? JSON.parse(claimStep.gvSchool)
              : claimStep.gvSchool;

          const schoolData = schoolObjectList.length
            ? schoolObjectList.map((item, index) => ({
                id: index + 1,
                regionName: item.RegionName,
                districtName: item.CityName,
                schoolId: item.SchoolId,
                schoolName: item.SchoolName,
              }))
            : null;

          return {
            requestTypeId: claimStep.requestTypePiloto ? 1 : 2,
            schoolData: schoolData,
            schoolDependencies: claimStep.schoolDependencies,
            requestSolicitorId: requestSolicitorId,
            university: claimStep.studySchool1
              ? {
                  id: claimStep.ddlUniversity,
                  name: claimStep.OtherRequestDetail,
                }
              : null,
            requestSolicitorDescription: [
              requestSchoolTypes.organization,
              requestSchoolTypes.other,
            ].includes(requestSolicitorId)
              ? claimStep.OtherRequestDetail
              : null,
          };
        }

        //RESEARCH_OBJECTIVES
        if (stepId === requestFormSteps.RESEARCH_OBJECTIVES && claimStep) {
          return {
            investigationTitle: claimStep.researchType,
            investigationObjective: claimStep.s2objective,
            investigationMethodology: claimStep.s2Methodology,
            investigationSamplingDescription: claimStep.s2Sample,
          };
        }
      }
    },
    getModelToSaveInfo(activeStepId) {
      //saved previous info
      const claimStep3Saved = this.autoSaveData?.data['ClaimStep3'];
      const claimStep4Saved = this.autoSaveData?.data['ClaimStep4'];

      const stepModel = this.model.step;
      const claimStep2 = stepModel[requestFormSteps.RESPONSIBLE_INFORMATION];
      const claimStep3 = stepModel[requestFormSteps.RESEARCH_INFORMATION];
      const claimStep4 = stepModel[requestFormSteps.RESEARCH_OBJECTIVES];
      let requestSolicitorDescription = null;

      if (claimStep3) {
        requestSolicitorDescription =
          claimStep3.requestSolicitorId === requestSchoolTypes.school
            ? claimStep3.university?.name
            : claimStep3.requestSolicitorId === requestSchoolTypes.organization
            ? claimStep3.requestSolicitorDescription
            : claimStep3.requestSolicitorDescription;
      }

      const modelToSave = {
        ClaimStep2: claimStep2
          ? {
              mentorFullName: claimStep2.mentorFullName,
              mentorJobPosition: claimStep2.mentorWorkPosition,
              mentorWorkplace: claimStep2.mentorWorkPlace,
              mentorPhoneNumber: claimStep2.mentorTelephone,
              mentorCellphone: claimStep2.mentorCellPhone,
              mentorEmail: claimStep2.mentorEmail,
            }
          : null,
        ClaimStep3: claimStep3
          ? {
              requestTypePiloto:
                claimStep3.requestTypeId ===
                itemSubTypes.ValidateInstrumentsOrPilotTest,
              requestTypeInvestigation:
                claimStep3.requestTypeId === itemSubTypes.ConductResearch,
              requestTypePilotoReadOnly: false,
              requestTypeInvestigationReadOnly: false,
              gvSchool: JSON.stringify(
                claimStep3.schoolsData.map((item, index) => ({
                  OrderNumber: index + 1,
                  RegionName: item.region?.name,
                  DistrictName: item.municipality?.name,
                  CityName: item.municipality?.name,
                  SchoolId: item.school?.id,
                  SchoolName: item.school?.name,
                }))
              ),
              schoolDependencies: claimStep3.schoolDependencies,
              studySchool1:
                claimStep3.requestSolicitorId === requestSchoolTypes.school,
              organizationName1:
                claimStep3.requestSolicitorId ===
                requestSchoolTypes.organization,
              otherRequest1:
                claimStep3.requestSolicitorId === requestSchoolTypes.other,
              OtherRequestDetail: requestSolicitorDescription,
              ddlUniversity: claimStep3.university?.id,
            }
          : {
              ...claimStep3Saved,
              gvSchool: claimStep3Saved?.gvSchool
                ? claimStep3Saved.gvSchool
                : null,
            },
        ClaimStep4: claimStep4
          ? {
              researchType: claimStep4.investigationTitle,
              s2objective: claimStep4.investigationObjective,
              s2Methodology: claimStep4.investigationMethodology,
              s2Sample: claimStep4.investigationSamplingDescription,
            }
          : claimStep4Saved,
        ActiveStep: activeStepId,
        IsFromDatabase: true,
      };
      return JSON.stringify(modelToSave);
    },
  },
};
</script>

<style scoped lang="scss">
.min-height-content {
  min-height: 65vh !important;
}

.navigation-container {
  max-width: 400px;
}

@media (max-width: 991px) {
  .min-height-content {
    min-height: unset !important;
    margin-bottom: 50px;
  }
  .navigation-container {
    max-width: 100%;
    &__steps {
      max-height: 180px !important;
      overflow-x: scroll;
    }
  }
}
</style>
